import React, { useState, useEffect, useRef } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { Modal, Button, Input, Textarea } from '../ui';
import type { Compound, Offering, CreateOfferingData } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateOfferingData) => Promise<void>;
  compounds: Compound[];
  offering?: Offering | null;
  isLoading?: boolean;
}

export const AddOfferingModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  compounds,
  offering,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CreateOfferingData>({
    compoundId: '',
    dosageVarietyId: '',
    dispenseUnitId: '',
    name: '',
    description: '',
    imageUrl: '',
    price: 0,
    quantity: 1,
    monthSupply: 1,
    quantityType: 'per_month',
    isActive: true,
    mdiCompoundId: '',
    pharmacyMedicationId: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const selectedCompound = compounds.find(c => c.id === formData.compoundId);
  const selectedDosage = selectedCompound?.dosageVarieties?.find(d => d.id === formData.dosageVarietyId);

  useEffect(() => {
    if (offering) {
      setFormData({
        compoundId: offering.compoundId,
        dosageVarietyId: offering.dosageVarietyId,
        dispenseUnitId: offering.dispenseUnitId,
        name: offering.offeringName,
        description: offering.offeringDescription,
        imageUrl: offering.offeringImageUrl || '',
        price: offering.price,
        quantity: offering.quantity,
        monthSupply: offering.monthSupply,
        quantityType: offering.quantityType || 'per_month',
        isActive: offering.isActive,
        mdiCompoundId: offering.mdiCompoundId || '',
        pharmacyMedicationId: offering.pharmacyMedicationId || '',
      });
      if (offering.offeringImageUrl) {
        setPhotoPreview(offering.offeringImageUrl);
      }
    } else {
      setFormData({
        compoundId: '',
        dosageVarietyId: '',
        dispenseUnitId: '',
        name: '',
        description: '',
        imageUrl: '',
        price: 0,
        quantity: 1,
        monthSupply: 1,
        quantityType: 'per_month',
        isActive: true,
        mdiCompoundId: '',
      });
      setPhotoPreview(null);
    }
  }, [offering, isOpen]);

  // Auto-select dispense unit when dosage variety changes
  useEffect(() => {
    if (selectedDosage?.dispenseUnit) {
      setFormData(prev => ({
        ...prev,
        dispenseUnitId: selectedDosage.dispenseUnit!.id,
      }));
    }
  }, [selectedDosage]);

  const handleCompoundChange = (compoundId: string) => {
    setFormData(prev => ({
      ...prev,
      compoundId,
      dosageVarietyId: '',
      dispenseUnitId: '',
    }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
        setFormData(prev => ({ ...prev, imageUrl: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    setFormData(prev => ({ ...prev, imageUrl: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      // Error handling done by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="lg"
      title={offering ? 'Edit Offering' : 'Add New Offering'}
    >
      <form onSubmit={handleSubmit} className="space-y-6 max-h-[calc(90vh-120px)] overflow-y-auto">
        {/* Compound Selection */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Compound *
          </label>
          <select
            value={formData.compoundId}
            onChange={(e) => handleCompoundChange(e.target.value)}
            className="w-full rounded-lg border border-secondary-300 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            required
          >
            <option value="">Select a compound</option>
            {compounds.map((compound) => (
              <option key={compound.id} value={compound.id}>
                {compound.name}
              </option>
            ))}
          </select>
        </div>

        {/* Dosage Variety Selection */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Dosage Variety *
          </label>
          <select
            value={formData.dosageVarietyId}
            onChange={(e) => setFormData(prev => ({ ...prev, dosageVarietyId: e.target.value }))}
            className="w-full rounded-lg border border-secondary-300 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-secondary-50 disabled:text-secondary-400"
            required
            disabled={!formData.compoundId}
          >
            <option value="">Select a dosage variety</option>
            {selectedCompound?.dosageVarieties?.map((dosage) => (
              <option key={dosage.id} value={dosage.id}>
                {dosage.dosage} - {dosage.form}
              </option>
            ))}
          </select>
          {formData.compoundId && selectedCompound && !selectedCompound.dosageVarieties?.length && (
            <p className="mt-1 text-xs text-amber-600">
              No dosage varieties available for this compound
            </p>
          )}
        </div>

        {/* Dispense Unit (Auto-filled) */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Dispense Unit
          </label>
          <input
            type="text"
            value={selectedDosage?.dispenseUnit?.name || ''}
            className="w-full rounded-lg border border-secondary-300 px-3 py-2.5 text-sm bg-secondary-50 text-secondary-500"
            disabled
            placeholder="Auto-filled based on dosage variety"
          />
        </div>

        {/* Photo Upload */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Product Image
          </label>
          <div className="flex items-start space-x-4">
            <div className="w-20 h-20 rounded-lg bg-secondary-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-secondary-300">
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <PhotoIcon className="h-8 w-8 text-secondary-400" />
              )}
            </div>
            <div className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload Image
              </Button>
              {photoPreview && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemovePhoto}
                  className="ml-2"
                >
                  Remove
                </Button>
              )}
              <p className="mt-1 text-xs text-secondary-500">PNG, JPG up to 5MB</p>
            </div>
          </div>
        </div>

        {/* Offering Name */}
        <Input
          label="Offering Name *"
          placeholder="e.g., Sildenafil 100mg - 30 Day Supply"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          required
        />

        {/* Description */}
        <Textarea
          label="Description *"
          placeholder="Enter offering description..."
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          required
        />

        {/* Pricing and Supply */}
        <div className="grid grid-cols-3 gap-4">
          <Input
            label="Price ($) *"
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={formData.price}
            onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
            required
          />
          <Input
            label="Quantity *"
            type="number"
            min="1"
            placeholder="1"
            value={formData.quantity}
            onChange={(e) => setFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
            required
          />
          <Input
            label="Month Supply *"
            type="number"
            min="1"
            placeholder="1"
            value={formData.monthSupply}
            onChange={(e) => setFormData(prev => ({ ...prev, monthSupply: parseInt(e.target.value) || 1 }))}
            required
          />
        </div>

        {/* Quantity Type */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Quantity Type *
          </label>
          <select
            value={formData.quantityType}
            onChange={(e) => setFormData(prev => ({ ...prev, quantityType: e.target.value as 'per_month' | 'total' }))}
            className="w-full rounded-lg border border-secondary-300 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            required
          >
            <option value="per_month">Per Month (quantity × monthSupply)</option>
            <option value="total">Total (quantity is total for entire supply)</option>
          </select>
          <p className="mt-1 text-xs text-secondary-500">
            Per Month: e.g., 6 pills/month × 3 months = 18 total. Total: e.g., 1 injection for 3 months = 1 total.
          </p>
        </div>

        {/* Pharmacy Medication ID (external product id at the fulfilling pharmacy, e.g. PCP Health) */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Pharmacy Medication ID
          </label>
          <input
            type="text"
            value={formData.pharmacyMedicationId || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, pharmacyMedicationId: e.target.value }))}
            placeholder="Required for PCP Health offerings (the pharmacy's medication_id)"
            className="w-full rounded-lg border border-secondary-300 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <p className="mt-1 text-xs text-secondary-500">
            External product identifier at the fulfilling pharmacy. Leave blank for Curexa; set the PCP Health medication_id when the medication is assigned to PCP Health.
          </p>
        </div>

        {/* Active Status */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
          />
          <label htmlFor="isActive" className="ml-2 block text-sm text-secondary-700">
            Active offering (visible to customers)
          </label>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-100">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting || isLoading}>
            {offering ? 'Update Offering' : 'Create Offering'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddOfferingModal;
